import { siteApi } from '@/services';
import Events from '../constants/events';
import app from './app';
import config from './config';
import { consentState } from './consentState';
import { complianceRequests } from './complianceRequests';
import { handleError } from '../utilities';
import { getName, isDomainMatch } from '../utilities/nodes';
import { ConsentType } from '@/types';

type Tracker = {
  category: string;
  name: string;
  pattern: string;
  type: string;
  regex: RegExp;
  modified?: 'allowed' | 'blocked';
  previousModified?: 'allowed' | 'blocked';
  modifiedChanged?: boolean;
};

type Store = {
  loaded: boolean;
  ready: boolean;
  app: typeof app;
  config: typeof config;
  consentState: typeof consentState;
  consentTypes: ConsentType[];
  complianceRequests: typeof complianceRequests;
  domainsSet: Set<string>;
  gcmMode: 'disabled' | 'basic' | 'advanced';
  unavailable: boolean;
  removedNodes: Element[];
  knownTrackers: Tracker[];
  newTrackers: Omit<Tracker, 'regex'>[];
  lastRefreshDate?: Date;

  initialize(params?: unknown): Promise<void>;
  runCommand(command: string, params?: unknown): Promise<void>;
  refreshSession(): Promise<void>;
  refreshData(): Promise<void>;
  updateRemovedNodes(newNodes: Element[]): void;
  getRemovedNodes(): Element[];
  setKnownTrackers(trackers: Omit<Tracker, 'regex'>[]): Promise<void>;
  getKnownTrackers(): Tracker[];
  isKnownTracker(tracker: Pick<Tracker, 'name' | 'type'>): Tracker | undefined;
  addSiteTracker(tracker: Omit<Tracker, 'regex'>): void;
  uploadSiteTrackers(): void;
  updateTrackersModified(): void;
};

export default {
  loaded: false,
  ready: false,
  app,
  config,
  consentState,
  consentTypes: [],
  complianceRequests,
  domainsSet: new Set(),
  gcmMode: 'disabled',
  unavailable: false,
  removedNodes: [],
  knownTrackers: [],
  newTrackers: [],

  async initialize(params) {
    this.loaded = true;
    await this.runCommand('initialize', params);
    await this.refreshData();
    this.ready = true;
    window.dispatchEvent(new CustomEvent(Events.Ready));
  },

  async runCommand(command, params) {
    await Promise.all(
      Object.values(this)
        // @ts-ignore
        .filter((store) => !!store[command])
        // @ts-ignore
        .map(async (store) => store[command](params)),
    );
  },

  async refreshSession() {
    return this.runCommand('refreshSession');
  },

  async refreshData() {
    this.lastRefreshDate = new Date();
    return this.runCommand('refreshData');
  },

  updateRemovedNodes(newNodes) {
    this.removedNodes = newNodes;
  },

  getRemovedNodes() {
    return this.removedNodes;
  },

  async setKnownTrackers(trackers) {
    /* Sort the trackers with scripts, iframes, links, then cookies.
    5 is a fallback for anything else. */
    const order = new Map([
      ['script', 1],
      ['iframe', 2],
      ['image', 3],
      ['link', 4],
      ['cookie', 5],
    ]);

    trackers.sort(
      (a, b) => (order.get(a.type) || 5) - (order.get(b.type) || 5),
    );

    // Create a cache for regex patterns to avoid recompilation
    const regexCache = new Map();

    this.knownTrackers = trackers.map(
      ({ category, name, pattern, type, ...tracker }) => {
        // Check if we already have this pattern compiled
        if (!regexCache.has(pattern)) {
          regexCache.set(pattern, new RegExp(pattern));
        }
        return {
          ...tracker,
          category,
          name,
          pattern,
          type,
          regex: regexCache.get(pattern),
        };
      },
    );
  },

  async setConsentTypes(consentTypes: ConsentType[]) {
    window.dispatchEvent(new CustomEvent(Events.ConsentTypesLoaded));
    return (this.consentTypes = consentTypes);
  },

  getKnownTrackers() {
    return this.knownTrackers;
  },

  /** Check if a tracker is already in the known trackers list for the project. */
  isKnownTracker({ name, type }) {
    // First filter by type which is a direct comparison, then do regex
    const typeMatches = this.knownTrackers.filter(
      (tracker) => tracker.type === type,
    );

    // Only perform regex operations on type matches
    return typeMatches.find(({ regex }) => regex && name.match(regex) !== null);
  },

  /**
   * Add a tracker to the list of trackers to be uploaded. If the tracker is
   * already known or is a first party non-cookie source, it will not be added.
   * Trackers are added in order of type: script, iframe, image, link, cookie,
   * other. This is done to ensure that scripts are processed before iframe
   * nodes.
   *
   * @param tracker - The tracker object containing details
   * @param tracker.name - The name of the tracker
   * @param tracker.type - The type of tracker: script, iframe, image, link,
   *   cookie
   * @param tracker.category - The category of the tracker: advertising,
   *   analytics, etc.
   */
  addSiteTracker(tracker) {
    /* Skip first party sources if not a cookie */
    if (tracker.type !== 'cookie') {
      try {
        const srcUrl = new URL(tracker.name);
        const trackerName = getName(tracker.name);
        tracker.name = trackerName;
        if (isDomainMatch(srcUrl.hostname, this.domainsSet)) {
          return;
        }
      } catch {
        /* Invalid URLs are treated as non-first-party sources and continue processing */
      }
    }

    if (tracker?.name?.length > 256) {
      handleError(
        {
          message: `Tracker name exceeds 256 characters and will not be added: ${tracker.name}`,
        },
        'info',
      );
      return;
    }

    if (tracker?.name?.length !== 0 && !this.isKnownTracker(tracker)) {
      this.newTrackers.push(tracker);

      const order = new Map([
        ['script', 1],
        ['iframe', 2],
        ['image', 3],
        ['link', 4],
        ['cookie', 5],
      ]);

      const trackerOrder = order.get(tracker.type) || 5;

      const insertIndex = this.knownTrackers.findIndex(
        ({ type }) => (order.get(type) || 5) > trackerOrder,
      );

      const newTracker = {
        regex: new RegExp(tracker?.name),
        ...tracker,
      };

      /* Add the tracker to the known trackers list in the correct order. */
      if (insertIndex === -1) {
        this.knownTrackers.push(newTracker);
      } else {
        this.knownTrackers.splice(insertIndex, 0, newTracker);
      }
    }
  },

  /**
   * Upload all new site trackers to the API. This will upload all unknown
   * trackers identified above and then clear the trackers array. It uses a set
   * to ensure that each new tracker is only uploaded once. If the
   * window.concord.ready state is not ready, the function will wait for the
   * concord-ready event before attempting to upload the trackers.
   */

  uploadSiteTrackers() {
    const upload = () => {
      if (this.newTrackers.length) {
        // Create a unique key for each tracker to properly deduplicate
        const trackerMap = new Map();
        this.newTrackers.forEach((tracker) => {
          const key = `${tracker.name}|${tracker.type}`;
          trackerMap.set(key, tracker);
        });
        const uniqueTrackers = Array.from(trackerMap.values());
        siteApi.postTrackers({ items: uniqueTrackers });
        this.newTrackers = [];
      }
      window.removeEventListener('concord-ready', upload);
    };

    if (typeof window === 'undefined') {
      return;
    }

    if (window.concord?.ready) {
      upload();
      return;
    }

    if (window?.addEventListener) {
      window.addEventListener('concord-ready', upload);
    }
  },

  /**
   * Update the modified state of the known trackers based on the current
   * consent state. This will set the modified state to allowed if the tracker
   * is in a category that is allowed by the user.
   */

  updateTrackersModified() {
    this.knownTrackers = this.knownTrackers.map((tracker) => {
      // @ts-ignore
      const mode = this.config?.regionTemplate?.settings?.consent?.blockingMode;
      const newModified =
        tracker.category === 'ignored' ||
        tracker.category === 'strictly_necessary' ||
        (tracker.category === 'unclassified' && mode === 'permissive') ||
        // @ts-ignore
        this.consentState.allowState[tracker.category]
          ? 'allowed'
          : 'blocked';
      return {
        ...tracker,
        modified: newModified,
        previousModified: tracker.modified,
        modifiedChanged: newModified !== tracker.modified,
      };
    });
  },
} as Store;
